<script>
  import { FormTheme, Loader } from "@gl/design-system";
  import {
    Navbar,
    Callout,
    NewVersionCallout,
    DevProxyPanel,
  } from "./components/index";
  import { Router, Route } from "svelte-routing";
  import {
    Groups,
    Roles,
    Users,
    Applications,
    User,
    Role,
    Group,
    App,
    Home,
    Page404,
    Apps,
    AdminApp,
    AdminContracts,
    AdminAuthorizers,
    TemporarilyUnavailable,
    Root,
    Forbidden,
    Contracts,
    Login,
    Me,
    AMHome,
  } from "./pages/index";
  import {
    user,
    contract,
    language,
    navbarItems,
    theme,
    Auth,
    callout,
    _,
  } from "./stores/index";
  import { setContext } from "svelte";
  import { api, config } from "./services";
  import "./app.css";
  import { writable } from "svelte/store";
  import { addMessages, init, locale } from "svelte-i18n";
  import en from "./i18n/en.json";
  import de from "./i18n/de.json";
  import { isBefore } from "date-fns";
  import getFile from "./services/xml";
  import { checkVersions } from "./utils";
  import "@gl/design-system/css";
  import { useStack } from "@gl/svelte/usables";
  import EventManagerContext from "./EventManagerContext.svelte";
  import CutManagerContext from "./CutManagerContext.svelte";
  import ContentManagerContext from "./ContentManagerContext.svelte";
  import {
    getIDP,
    isLoginCallback,
    handleLoginCallback,
  } from "./services/IDP";
  const IDP = getIDP();

  addMessages("en", en);
  addMessages("de", de);
  init({ fallbackLocale: "en", initialLocale: language });

  setContext("config", config);
  setContext("FormTheme", FormTheme);

  const itemsStore = writable([
    "access-manager",
    "channel-manager",
    "cut-manager",
    "content-manager",
  ]);
  const navbarItemsStore = useStack("GLOBAL_FE_NAVBAR_ITEMS", itemsStore);
  $: if (navbarItemsStore) navbarItems.set($navbarItemsStore);

  locale.set($language ?? "en");

  api.init({ apiurl: config.API_URL });

  const authClass = new Auth(user, "access-manager");
  setContext("isAuthorized", authClass);

  let newVersion = false;

  $: {
    const pathMatch = window.location.pathname.match(/\/contracts\/([^/]+)/);
    const pathContractId = pathMatch?.[1];
    if (pathContractId && $contract?.id !== pathContractId) {
      const userContract = $user?.contracts?.find(
        (c) => c.id === pathContractId,
      );
      contract.set(userContract);
    }
  }

  const load = async () => {
    await checkVersions(
      [
        "/index.html",
        ...(import.meta.env.MODE === "LOCAL_EVENT_MANAGER"
          ? []
          : ["/event-manager-frontend/event-manager.es.js"]),
      ],
      () => (newVersion = true),
    );
    if (isLoginCallback()) {
      try {
        await handleLoginCallback();
      } catch (e) {
        console.error("login callback failed:", e);
        callout.set({ msg: $_("login.failed"), status: "error" });
      }
      return;
    }

    if ($user?.token) {
      const now = new Date();
      if (isBefore(new Date($user.token.exp), now)) {
        await IDP.getToken();
      } else {
        IDP.init();
      }
    }
  };
</script>

{#if newVersion}
  <NewVersionCallout />
{/if}

<!-- Outside the load() boundary so it stays reachable if startup fails. -->
<DevProxyPanel />

{#await load()}
  <Loader />
{:then}
  <div class="root" class:theme-dark={$theme === "dark"}>
    <Router>
      {#if $user && $contract}
        <Navbar />
      {/if}
      <Route component={Forbidden} path="/forbidden" />

      {#if $user && $contract && $user.apps}
        <div class="content-container">
          <!-- global routes -->
          <Route path="/" component={Root} />
          <Route component={Page404} />
          <Route
            component={TemporarilyUnavailable}
            path="/temporarily-unavailable"
          />
          <Route component={Me} path="/me/*" />

          <Route path="/contracts" component={Contracts} />
          <Route path="/contracts/:CON" component={Home} />

          <!-- access manager routes -->
          <Route
            path="/access-manager/admin/contracts"
            component={AdminContracts}
          />
          <Route
            path="/access-manager/admin/authorizers"
            component={AdminAuthorizers}
          />
          <Route path="/access-manager/admin/apps" component={Apps} />
          <Route path="/access-manager/admin/apps/:id/*" let:params>
            <AdminApp id={params.id} />
          </Route>

          <Route path="/access-manager/contracts/:CON" component={AMHome} />
          <Route
            path="/access-manager/contracts/:CON/users"
            component={Users}
          />
          <Route path="/access-manager/contracts/:CON/users/:id/*" let:params>
            <User id={params.id} />
          </Route>

          <Route
            path="/access-manager/contracts/:CON/roles"
            component={Roles}
          />
          <Route path="/access-manager/contracts/:CON/roles/:id/*" let:params>
            <Role id={params.id} />
          </Route>

          <Route
            path="/access-manager/contracts/:CON/groups"
            component={Groups}
          />
          <Route path="/access-manager/contracts/:CON/groups/:id/*" let:params>
            <Group id={params.id} />
          </Route>

          <Route
            path="/access-manager/contracts/:CON/applications"
            component={Applications}
          />
          <Route
            path="/access-manager/contracts/:CON/applications/:id/*"
            let:params
          >
            <App id={params.id} />
          </Route>

          <!-- microservice routes -->
          {#if $user.apps?.some((app) => app.id === "channel-manager")}
            <Route path="/channel-manager/*">
              <EventManagerContext />
            </Route>
          {/if}

          {#if $user.apps?.some((app) => app.id === "cut-manager")}
            <Route path="/cut-manager/*">
              <CutManagerContext />
            </Route>
          {/if}

          {#if $user.apps?.some((app) => app.id === "content-manager")}
            <Route path="/content-manager/contracts/:CON/*">
              <ContentManagerContext />
            </Route>
          {/if}
        </div>
      {:else if $user && $user.data}
        <Route path="/" component={Contracts} />
        <Route path="*" component={Page404} />
      {:else}
        <Route path="/" component={Login} />
        <Route path="*" component={Login} />
      {/if}
    </Router>

    <Callout />
  </div>
{/await}

<style>
  .root {
    display: grid;
    grid-template-rows: auto 1fr;
    height: 100%;
    color-scheme: light;
  }

  .root.theme-dark {
    color-scheme: dark;
  }

  .content-container {
    height: 100%;
    max-width: 100vw;
    overflow: auto;
  }
</style>
